/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-07
 */

import { ErrorType } from '../components/common/ErrorAlert';

// Categorized error result interface
export interface CategorizedError {
  message: string;
  type: ErrorType;
  isRetryable: boolean;
}

/**
 * Categorizes API errors and provides user-friendly messages.
 *
 * @param error - RTK Query error object
 * @returns Categorized error with user-friendly message and type
 */
export const categorizeError = (error: any): CategorizedError => {
  // Handle network/fetch errors
  if (error?.error && typeof error.error === 'string') {
    if (error.error.includes('fetch')) {
      return {
        message:
          'Unable to connect to the server. Please check your internet connection and try again.',
        type: 'network',
        isRetryable: true,
      };
    }
  }

  // Handle HTTP status codes
  if (error?.status) {
    const status =
      typeof error.status === 'string'
        ? parseInt(error.status, 10)
        : error.status;

    switch (status) {
      case 400:
        return {
          message:
            error.data?.error ||
            error.data?.detail ||
            'Invalid request. Please check your input and try again.',
          type: 'validation',
          isRetryable: false,
        };

      case 401:
        return {
          message: 'Authentication required. Please log in and try again.',
          type: 'client',
          isRetryable: false,
        };

      case 403:
        return {
          message:
            'Access denied. You do not have permission to perform this action.',
          type: 'client',
          isRetryable: false,
        };

      case 404:
        return {
          message: 'The requested resource was not found.',
          type: 'client',
          isRetryable: false,
        };

      case 429:
        return {
          message:
            'Too many requests. Please wait a moment before trying again.',
          type: 'ratelimit',
          isRetryable: true,
        };

      case 500:
        return {
          message: 'Internal server error. Please try again later.',
          type: 'server',
          isRetryable: true,
        };

      case 502:
        return {
          message:
            'Server temporarily unavailable. Please try again in a few moments.',
          type: 'server',
          isRetryable: true,
        };

      case 503:
        return {
          message: 'Service temporarily unavailable. Please try again later.',
          type: 'server',
          isRetryable: true,
        };

      case 504:
        return {
          message: 'Request timeout. Please try again.',
          type: 'server',
          isRetryable: true,
        };

      default:
        // Handle other 4xx errors as client errors
        if (status >= 400 && status < 500) {
          return {
            message:
              error.data?.error ||
              error.data?.detail ||
              'Request failed. Please check your input and try again.',
            type: 'client',
            isRetryable: false,
          };
        }

        // Handle other 5xx errors as server errors
        if (status >= 500) {
          return {
            message: 'Server error occurred. Please try again later.',
            type: 'server',
            isRetryable: true,
          };
        }
    }
  }

  // Handle specific error messages
  if (error?.data?.error) {
    const errorMessage = error.data.error.toLowerCase();

    if (errorMessage.includes('csrf')) {
      return {
        message:
          'Security token expired. Please refresh the page and try again.',
        type: 'client',
        isRetryable: true,
      };
    }

    if (
      errorMessage.includes('turnstile') ||
      errorMessage.includes('captcha')
    ) {
      return {
        message:
          'Verification failed. Please complete the verification and try again.',
        type: 'validation',
        isRetryable: true,
      };
    }

    if (
      errorMessage.includes('rate limit') ||
      errorMessage.includes('too many')
    ) {
      return {
        message: 'Too many requests. Please wait a moment before trying again.',
        type: 'ratelimit',
        isRetryable: true,
      };
    }

    // Return the specific error message if available
    return {
      message: error.data.error,
      type: 'unknown',
      isRetryable: true,
    };
  }

  // Fallback for unknown errors
  return {
    message: 'An unexpected error occurred. Please try again.',
    type: 'unknown',
    isRetryable: true,
  };
};

/**
 * Returns a user-facing message for an RTK Query error: the backend's
 * `detail` string, else the first field error. Server errors (5xx) and
 * anything unrecognised return the fallback, so raw server exception text
 * is never shown.
 *
 * @param error - Error thrown by an RTK Query `unwrap()`
 * @param fallback - Message to show when no safe backend message exists
 * @returns The message to display
 */
export const getApiErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  if (!error || typeof error !== 'object') return fallback;

  const { status, data } = error as { status?: unknown; data?: unknown };
  if (typeof status !== 'number' || status >= 500) return fallback;
  if (!data || typeof data !== 'object') return fallback;

  const { detail } = data as { detail?: unknown };
  if (typeof detail === 'string' && detail) return detail;

  const [firstField] = Object.values(data);
  const message = Array.isArray(firstField) ? firstField[0] : firstField;
  return typeof message === 'string' && message ? message : fallback;
};

/**
 * Checks if the user is currently online.
 *
 * @returns True if online, false if offline
 */
export const isOnline = (): boolean => navigator.onLine;

/**
 * Detects if an error is likely due to network connectivity issues.
 *
 * @param error - RTK Query error object
 * @returns True if the error appears to be network-related
 */
export const isNetworkError = (error: any): boolean => {
  // Check if user is offline
  if (!isOnline()) {
    return true;
  }

  // Check for fetch errors
  if (error?.error && typeof error.error === 'string') {
    const errorString = error.error.toLowerCase();
    return (
      errorString.includes('fetch') ||
      errorString.includes('network') ||
      errorString.includes('connection') ||
      errorString.includes('timeout')
    );
  }

  // Check for network-related status codes
  if (error?.status === 0 || error?.status === 'FETCH_ERROR') {
    return true;
  }

  return false;
};

/**
 * Gets a retry delay based on error type (in milliseconds).
 *
 * @param errorType - The categorized error type
 * @param attemptNumber - The current retry attempt number (1-based)
 * @returns Delay in milliseconds before retry
 */
export const getRetryDelay = (
  errorType: ErrorType,
  attemptNumber: number = 1
): number => {
  const baseDelay = 1000; // 1 second base delay

  switch (errorType) {
    case 'network':
      // Exponential backoff for network errors
      return baseDelay * 2 ** (attemptNumber - 1);

    case 'server':
      // Longer delay for server errors
      return baseDelay * 2 * attemptNumber;

    case 'ratelimit':
      // Longer delay for rate limiting
      return baseDelay * 5 * attemptNumber;

    default:
      return baseDelay;
  }
};
